// HistoriqueModal — Timeline de toutes les modifications d'un dossier
import { X, Clock, User, FileText, ArrowRight } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";

export function HistoriqueModal({ historique = [], onClose }) {
  // Sort by date descending
  const sorted = [...historique].sort((a, b) => {
    const da = new Date(a.date || a.created_at || 0);
    const db = new Date(b.date || b.created_at || 0);
    return db - da;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="historique-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[var(--oh-bg)] backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg max-h-[80vh] rounded-xl overflow-hidden flex flex-col"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid var(--oh-border)" }}>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
            <h2 className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
              Historique des modifications
            </h2>
            <Badge className="text-[10px] bg-zinc-500/20 border-[var(--oh-border)]">{sorted.length}</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-historique">
            <X className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
          </Button>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto p-5">
          {sorted.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: "var(--oh-text-muted)" }}>
              Aucune modification enregistrée
            </p>
          ) : (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-3 top-2 bottom-2 w-px" style={{ background: "var(--oh-border)" }} />

              <div className="space-y-4">
                {sorted.map((entry, idx) => {
                  const date = entry.date || entry.created_at;
                  const formattedDate = date ? new Date(date).toLocaleDateString("fr-FR", {
                    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                  }) : "—";

                  const actionType = entry.action || entry.type || "modification";
                  const actionColor = actionType === "creation" ? "#10b981"
                    : actionType === "transition" ? "var(--oh-stat-blue-text)"
                    : actionType === "agent_update" ? "#8b5cf6"
                    : "var(--oh-text-muted)";

                  return (
                    <div key={idx} className="relative pl-8" data-testid={`historique-entry-${idx}`}>
                      {/* Dot */}
                      <div
                        className="absolute left-1.5 top-1 w-3 h-3 rounded-full border-2"
                        style={{ background: "var(--oh-bg-elevated)", borderColor: actionColor }}
                      />

                      <div className="rounded-lg p-3" style={{ background: "var(--oh-bg)", border: "1px solid var(--oh-border)" }}>
                        <div className="flex items-center justify-between mb-1.5">
                          <Badge className="text-[10px] px-1.5 py-0 border-0" style={{ background: `${actionColor}20`, color: actionColor }}>
                            {actionType === "creation" ? "Création"
                              : actionType === "transition" ? "Transition"
                              : actionType === "agent_update" ? "Agent IA"
                              : actionType === "field_update" ? "Mise à jour"
                              : actionType}
                          </Badge>
                          <span className="text-[10px]" style={{ color: "var(--oh-text-muted)" }}>{formattedDate}</span>
                        </div>

                        {entry.description && (
                          <p className="text-xs mb-1" style={{ color: "var(--oh-text)" }}>{entry.description}</p>
                        )}

                        {entry.changes && typeof entry.changes === "object" && (
                          <div className="space-y-0.5 mt-1">
                            {Object.entries(entry.changes).slice(0, 5).map(([field, val]) => (
                              <div key={field} className="flex items-center gap-1 text-[10px]">
                                <span style={{ color: "var(--oh-text-muted)" }}>{field}:</span>
                                {val && typeof val === "object" && val.from !== undefined ? (
                                  <>
                                    <span style={{ color: "var(--oh-stat-red-text)" }}>{String(val.from || "—")}</span>
                                    <ArrowRight className="w-2.5 h-2.5" style={{ color: "var(--oh-text-muted)" }} />
                                    <span style={{ color: "#10b981" }}>{String(val.to || "—")}</span>
                                  </>
                                ) : (
                                  <span style={{ color: "var(--oh-text)" }}>{String(val)}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {entry.user_name && (
                          <div className="flex items-center gap-1 mt-1.5">
                            <User className="w-2.5 h-2.5" style={{ color: "var(--oh-text-muted)" }} />
                            <span className="text-[10px]" style={{ color: "var(--oh-text-muted)" }}>{entry.user_name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
